import React, { useState } from 'react';
import { ShieldCheckIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import ToastStack from '../components/ToastStack';
import { ROLE_DETAILS, Role, useAuth } from '../state/AuthContext';

const demoAccounts: Array<{ email: string; password: string; role: Role }> = [
  { email: 'director@lkcmedical.com', password: 'director123', role: 'medical_director' },
  { email: 'surgery@lkcmedical.com', password: 'surgery123', role: 'surgery' },
  { email: 'internal@lkcmedical.com', password: 'internal123', role: 'internal_med' },
  { email: 'frontdesk@lkcmedical.com', password: 'front123', role: 'customer_service' },
  { email: 'billing@lkcmedical.com', password: 'billing123', role: 'billing' },
];

const Login: React.FC = () => {
  const { login, status, error } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError(null);
    if (!email || !password) {
      setFormError('Please enter your email and password.');
      return;
    }
    try {
      await login({ email, password });
    } catch (err) {
      console.warn('Login failed', err);
      setFormError('Unable to sign in. Check your credentials and try again.');
    }
  };

  const isSubmitting = status === 'loading';

  const setDemoAccount = (account: { email: string; password: string }) => {
    setEmail(account.email);
    setPassword(account.password);
  };

  const helperText = formError ?? error;

  return (
    <div className="relative min-h-screen bg-slate-950">
      <ToastStack />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_#312e81,_#0f172a)] opacity-80" />
      <div className="relative z-10 flex min-h-screen flex-col items-center justify-center px-4 py-16">
        <div className="mx-auto w-full max-w-5xl grid gap-10 lg:grid-cols-[1.2fr_1fr]">
          <section className="rounded-3xl border border-white/10 bg-white/5 p-8 text-white shadow-2xl backdrop-blur">
            <div className="flex items-center gap-3 text-sm font-semibold uppercase tracking-wider text-slate-200">
              <ShieldCheckIcon className="h-5 w-5 text-brand-sky" />
              LKC Medical Command
            </div>
            <h1 className="mt-4 text-4xl font-semibold leading-tight">Commercial-grade animal hospital cockpit</h1>
            <p className="mt-4 text-sm text-slate-200">
              Sign in with one of the sample roles to explore real-time patient coordination, billing, and audit-ready workflows.
            </p>

            <div className="mt-8 grid gap-3 text-sm">
              {demoAccounts.map((account) => {
                const roleMeta = ROLE_DETAILS[account.role];
                return (
                  <button
                    key={account.email}
                    type="button"
                    onClick={() => setDemoAccount(account)}
                    className="flex items-center justify-between rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-left transition hover:border-brand-sky hover:bg-white/10"
                  >
                    <div>
                      <p className="font-semibold text-white">{roleMeta.label}</p>
                      <p className="text-xs text-slate-300">{account.email}</p>
                    </div>
                    <ArrowRightIcon className="h-4 w-4 text-slate-300" />
                  </button>
                );
              })}
            </div>
          </section>

          <section className="rounded-3xl bg-white p-8 shadow-2xl">
            <div className="mb-6">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Secure access</p>
              <h2 className="text-2xl font-semibold text-slate-900">Team sign-in</h2>
            </div>
            <form className="space-y-5" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="email" className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Work email
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  className="mt-1 w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-900 focus:border-brand-blue focus:ring-brand-blue"
                  placeholder="director@lkcmedical.com"
                  autoComplete="email"
                />
              </div>
              <div>
                <label htmlFor="password" className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  className="mt-1 w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-900 focus:border-brand-blue focus:ring-brand-blue"
                  placeholder="••••••••"
                  autoComplete="current-password"
                />
              </div>
              {helperText && <p className="text-sm font-medium text-rose-600">{helperText}</p>}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full rounded-2xl bg-gradient-to-r from-brand-blue to-brand-sky py-3 text-sm font-semibold text-white shadow-lg shadow-brand-blue/30 transition disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isSubmitting ? 'Signing in…' : 'Access console'}
              </button>
            </form>
            <p className="mt-6 text-xs text-slate-500">
              Access is audited and encrypted. Demo credentials reset automatically.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Login;
